import React, { useState, useMemo } from 'react';
import { doc, runTransaction, updateDoc } from 'firebase/firestore';
import { Bot, List, ShieldCheck, Lock, Unlock, UserX } from 'lucide-react';
import { db, APP_ID_INTERNO } from '../firebase';
import { calculateStandings } from '../utils';
import './Phase1Swiss.css';

export default function Phase1Swiss({ tournament, role }) {
    const [localScores, setLocalScores] = useState({});
    const topN = tournament.players.length >= 24 ? 16 : tournament.players.length >= 16 ? 8 : tournament.players.length >= 8 ? 4 : 2;

    const standings = useMemo(() => calculateStandings(tournament), [tournament]);
    const activeStandings = useMemo(() => standings.filter(p => !p.isDisqualified), [standings]);
    const dqPlayers = useMemo(() => standings.filter(p => p.isDisqualified), [standings]);

    const toggleDisqualify = async (playerId) => {
        if (role !== 'admin') return;
        const playerObj = tournament.players.find(p => p.id === playerId);
        const isDQ = !!playerObj?.isDisqualified;
        if (!window.confirm(`¿Seguro que quieres ${isDQ ? 'reintegrar' : 'descalificar'} a "${playerObj?.name}"?`)) return;

        try {
            const tRef = doc(db, 'torneos_beyblade', APP_ID_INTERNO, 'lista', tournament.id);
            await runTransaction(db, async (transaction) => {
                const sfDoc = await transaction.get(tRef);
                if (!sfDoc.exists()) return;
                const currentPlayers = sfDoc.data().players || [];
                const updatedPlayers = currentPlayers.map(p => 
                    p.id === playerId ? { ...p, isDisqualified: !isDQ } : p
                );
                transaction.update(tRef, { players: updatedPlayers });
            });
        } catch (e) {
            console.error("Error al descalificar/reintegrar jugador:", e);
        }
    };

    const handleLocalScoreChange = (mid, field, val) => {
        const match = tournament.phase1Matches.find(m => m.id === mid);
        const existingP1 = localScores[mid]?.p1Score ?? match?.p1Score ?? '';
        const existingP2 = localScores[mid]?.p2Score ?? match?.p2Score ?? '';
        setLocalScores(prev => ({
            ...prev,
            [mid]: {
                p1Score: field === 'p1Score' ? val : existingP1,
                p2Score: field === 'p2Score' ? val : existingP2
            }
        }));
    };

    const toggleLock = async (mid) => {
        if (role !== 'admin') return;
        const match = tournament.phase1Matches.find(m => m.id === mid);
        if (!match) return;

        const isCurrentlyLocked = !!match.isLocked;
        const p1Val = localScores[mid]?.p1Score ?? match.p1Score ?? '';
        const p2Val = localScores[mid]?.p2Score ?? match.p2Score ?? '';

        try {
            const tRef = doc(db, 'torneos_beyblade', APP_ID_INTERNO, 'lista', tournament.id);
            await runTransaction(db, async (transaction) => {
                const sfDoc = await transaction.get(tRef);
                if (!sfDoc.exists()) return;
                const currentMatches = sfDoc.data().phase1Matches || [];
                const newM = currentMatches.map(m => {
                    if (m.id === mid) {
                        if (isCurrentlyLocked) {
                            return { ...m, isLocked: false };
                        } else {
                            let up = { ...m, p1Score: p1Val, p2Score: p2Val, isLocked: true };
                            up.status = (up.p1Score !== '' && up.p2Score !== '') ? 'completed' : 'pending';
                            return up;
                        }
                    }
                    return m;
                });
                transaction.update(tRef, { phase1Matches: newM });
            });

            setLocalScores(prev => {
                const updated = { ...prev };
                delete updated[mid];
                return updated;
            });
        } catch (e) {
            console.error("Error al cambiar bloqueo/guardar puntuaciones en transacción:", e);
        }
    };

    const nextPhase = async () => {
        if (role !== 'admin') return;

        // DETECCIÓN DE EMPATE SUPREMO (sólo jugadores activos)
        let tieGroups = {};
        activeStandings.forEach(p => {
            let key = `${p.wins.toFixed(2)}_${p.pf.toFixed(2)}_${(p.pf - p.pa).toFixed(2)}`;
            if (!tieGroups[key]) tieGroups[key] = [];
            tieGroups[key].push(p);
        });

        let tiebreakerMatches = [];
        Object.values(tieGroups).forEach(group => {
            if (group.length > 1) {
                let needsTiebreaker = false;
                for (let i = 0; i < group.length; i++) {
                    for (let j = i + 1; j < group.length; j++) {
                        if (!group[i].h2h[group[j].id]) {
                            needsTiebreaker = true;
                        }
                    }
                }
                
                if (needsTiebreaker) {
                    for (let i = 0; i < group.length; i++) {
                        for (let j = i + 1; j < group.length; j++) {
                            tiebreakerMatches.push({
                                id: crypto.randomUUID(),
                                p1: group[i].id,
                                p2: group[j].id,
                                p1Score: '',
                                p2Score: '',
                                status: 'pending',
                                isLocked: false
                            });
                        }
                    }
                }
            }
        });

        if (tiebreakerMatches.length > 0) {
            if (!window.confirm(`¡EMPATE SUPREMO DETECTADO!\n\nHay jugadores activos con las mismas estadísticas que no se han enfrentado.\nSe requiere jugar un "Desempate Supremo" para decidir el orden.\n\n¿Ir a la fase de desempates?`)) return;
            
            await updateDoc(doc(db, 'torneos_beyblade', APP_ID_INTERNO, 'lista', tournament.id), { 
                phase: 'phase_tiebreaker', 
                tiebreakerMatches 
            });
            return; 
        }

        if (!window.confirm(`Clasificación clara. ¿Generar Eliminatorias (Top ${topN})?`)) return;
        
        let seeds = topN === 16 ? [1,16,8,9,4,13,5,12,2,15,7,10,3,14,6,11] : topN === 8 ? [1,8,4,5,2,7,3,6] : topN === 4 ? [1,4,2,3] : [1,2];
        let r1 = [];
        for (let i = 0; i < seeds.length; i+=2) {
            r1.push({ id: crypto.randomUUID(), p1: activeStandings[seeds[i]-1]?.id, p2: activeStandings[seeds[i+1]-1]?.id, p1Score: '', p2Score: '', status: 'pending', isLocked: false });
        }
        
        let bracket = [{ name: 'Eliminatorias', matches: r1 }];
        let prev = r1;
        while(prev.length > 1) {
            let next = Array(prev.length/2).fill().map(() => ({ id: crypto.randomUUID(), p1: null, p2: null, p1Score: '', p2Score: '', status: 'pending', isLocked: false }));
            bracket.push({ name: 'Fase Siguiente', matches: next });
            prev = next;
        }
        if (topN >= 4) {
            bracket.push({ name: '3er Puesto', matches: [{ id: crypto.randomUUID(), p1: null, p2: null, p1Score: '', p2Score: '', status: 'pending', isThird: true, isLocked: false }] });
        }
        
        await updateDoc(doc(db, 'torneos_beyblade', APP_ID_INTERNO, 'lista', tournament.id), { phase: 'phase2', phase2Bracket: bracket });
    };

    const getKubeySim = (match) => {
        const playerId = match.p1;
        const realMatches = tournament.phase1Matches.filter(m => !m.isKubey && m.status === 'completed' && (m.p1 === playerId || m.p2 === playerId));
        
        if (realMatches.length === 0) return { p1: '0.00', p2: '0.00' };

        let totalPf = 0;
        let totalPa = 0;

        realMatches.forEach(m => {
            if (m.p1 === playerId) {
                totalPf += Number(m.p1Score);
                totalPa += Number(m.p2Score);
            } else {
                totalPf += Number(m.p2Score);
                totalPa += Number(m.p1Score);
            }
        });

        return {
            p1: (totalPf / realMatches.length).toFixed(2),
            p2: (totalPa / realMatches.length).toFixed(2)
        };
    };

    return (
        <div className="swiss-layout">
            <div className="swiss-main">
                {tournament.players.length % 2 !== 0 && (
                    <div className="kubey-banner">
                        <div className="kubey-banner-content">
                            <h4 className="kubey-title">
                                <Bot className="w-6 h-6"/> Protocolo Kubey Activo
                            </h4>
                            <p className="kubey-subtitle">
                                Simulación basada en promedios de rendimiento real
                            </p>
                        </div>
                        <ShieldCheck className="kubey-icon-bg" />
                    </div>
                )}

                {[1, 2, 3, 4, 5].map(rNum => (
                    <div key={rNum} className="round-section">
                        <div className="round-header">
                            <span className="round-title">Ronda {rNum}</span>
                            <div className="round-divider"></div>
                        </div>
                        <div className="matches-grid">
                            {tournament.phase1Matches.filter(m => m.round === rNum).map((m, idx) => {
                                const isK = m.isKubey;
                                const sim = isK ? getKubeySim(m) : null;
                                const p1Obj = tournament.players.find(p=>p.id===m.p1);
                                const p2Obj = tournament.players.find(p=>p.id===m.p2);
                                return (
                                    <div key={m.id} className={`match-card ${isK ? 'is-kubey' : ''} ${!isK && m.status === 'completed' ? 'is-completed' : ''}`}>
                                        
                                        <span className="match-index">
                                            #{idx + 1}
                                        </span>

                                        <div className="match-player-name right">
                                            {p1Obj?.name || 'TBD'}
                                            {p1Obj?.isDisqualified && <span className="dq-badge">(DQ)</span>}
                                        </div>
                                        
                                        <div className="match-center">
                                            <div className="match-inputs-row">
                                                {isK ? (
                                                    <>
                                                        <div className="kubey-score">{sim.p1}</div>
                                                        <div className="kubey-score">{sim.p2}</div>
                                                    </>
                                                ) : (
                                                    <>
                                                        <input 
                                                            type="number" 
                                                            value={localScores[m.id]?.p1Score ?? m.p1Score} 
                                                            onChange={e => handleLocalScoreChange(m.id, 'p1Score', e.target.value)} 
                                                            disabled={role === 'guest' || m.isLocked} 
                                                            className="score-input" 
                                                            placeholder="-" 
                                                        />
                                                        <input 
                                                            type="number" 
                                                            value={localScores[m.id]?.p2Score ?? m.p2Score} 
                                                            onChange={e => handleLocalScoreChange(m.id, 'p2Score', e.target.value)} 
                                                            disabled={role === 'guest' || m.isLocked} 
                                                            className="score-input"  
                                                            placeholder="-" 
                                                        />
                                                    </>
                                                )}
                                            </div>
                                            {!isK && role === 'admin' && (
                                                <button 
                                                    onClick={() => toggleLock(m.id)}
                                                    className={`btn-match-lock ${m.isLocked ? 'is-locked' : 'is-unlocked'}`}
                                                >
                                                    {m.isLocked ? <><Unlock className="w-3 h-3"/> Modificar</> : <><Lock className="w-3 h-3"/> Introducir</>}
                                                </button>
                                            )}
                                        </div>

                                        <div className="match-player-name left">
                                            {isK ? (
                                                <span style={{color: '#3498db', display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'flex-end'}}>
                                                    <Bot className="w-4 h-4"/> Kubey
                                                </span>
                                            ) : (
                                                <>
                                                    {p2Obj?.name || 'TBD'}
                                                    {p2Obj?.isDisqualified && <span className="dq-badge">(DQ)</span>}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
            
            <div className="swiss-sidebar">
                <div className="standings-panel">
                    <h3 className="standings-title">
                        <List style={{color: 'var(--accent-red)'}}/> Clasificación
                    </h3>
                    <div className="standings-list">
                        <div className="standings-header">
                            <div className="standings-col-name">Blader</div>
                            <div className="standings-col-center">V</div>
                            <div className="standings-col-center">PF</div>
                            <div className="standings-col-center">Dif</div>
                        </div>
                        {activeStandings.map((p, i) => (
                            <div key={p.id} className={`standings-row ${i < topN ? 'is-top' : 'is-bottom'}`}>
                                <div className="standings-row-name">
                                    <span className="standings-rank">{i+1}</span>
                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</span>
                                    {role === 'admin' && (
                                        <button 
                                            onClick={() => toggleDisqualify(p.id)} 
                                            className="btn-dq-icon" 
                                            title="Descalificar jugador"
                                        >
                                            <UserX className="w-3.5 h-3.5" />
                                        </button>
                                    )}
                                </div>
                                <div className="standings-stat-win">{p.wins}</div>
                                <div className="standings-stat-pf">{p.pf.toFixed(2)}</div>
                                <div className="standings-stat-diff">{(p.pf - p.pa).toFixed(2)}</div>
                            </div>
                        ))}
                    </div>

                    {role === 'admin' && dqPlayers.length > 0 && (
                        <div className="dq-players-section" style={{ marginTop: '1.5rem', borderTop: '1px dashed rgba(255,255,255,0.15)', paddingTop: '0.75rem' }}>
                            <div style={{ fontSize: '0.7rem', color: '#e74c3c', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '0.5rem' }}>
                                Descalificados ({dqPlayers.length})
                            </div>
                            {dqPlayers.map(p => (
                                <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.75rem', padding: '0.25rem 0', color: 'var(--text-secondary)' }}>
                                    <span style={{ textDecoration: 'line-through' }}>{p.name}</span>
                                    <button 
                                        onClick={() => toggleDisqualify(p.id)} 
                                        style={{ background: 'rgba(46, 204, 113, 0.1)', border: '1px solid rgba(46, 204, 113, 0.4)', color: '#2ecc71', borderRadius: '6px', padding: '2px 6px', fontSize: '0.65rem', fontWeight: 'bold', cursor: 'pointer' }}
                                    >
                                        Reintegrar
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {role === 'admin' && (
                        <button onClick={nextPhase} className="btn-finish-swiss">
                            Finalizar Suizo
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}